import { ChevronDownIcon, CopyIcon, PhoneOff, Smartphone } from "lucide-react";

import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const MobileAppDropdown = ({
  appDownloadUrl,
  hasSignedUp = true,
  isReadOnly = false,
}: {
  appDownloadUrl?: string;
  hasSignedUp?: boolean;
  // Viewing surfaces (the application review portal) show the status as
  // plain text with no actions behind it.
  isReadOnly?: boolean;
}) => {
  /* Handlers */
  const handleCopyAppDownloadUrl = () => {
    if (!appDownloadUrl) return;
    void navigator.clipboard.writeText(appDownloadUrl);
  };

  /* Render */
  const renderAppUser = () => {
    return (
      <Row className="items-center gap-2">
        <Smartphone
          className="h-4 w-4 shrink-0 text-slate-400"
          strokeWidth={1.5}
        />
        <Text className="text-xs font-normal text-slate-500">App user</Text>
      </Row>
    );
  };

  const renderPendingUserDropdown = () => {
    const iconClassName = "h-4 w-4 shrink-0 text-slate-400";
    const labelClassName =
      "text-xs font-normal text-slate-500 group-hover/pending-app:text-slate-950 group-data-[state=open]/pending-app:text-slate-950";
    const buttonClassName =
      "group/pending-app -m-2 h-auto gap-2 rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-950 data-[state=open]:bg-slate-100 data-[state=open]:text-slate-950";

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button className={buttonClassName} type="button" variant="ghost">
            <PhoneOff
              className={`${iconClassName} group-hover/pending-app:hidden group-data-[state=open]/pending-app:hidden`}
              strokeWidth={1.5}
            />
            <ChevronDownIcon
              className={`${iconClassName} hidden group-hover/pending-app:block group-hover/pending-app:text-slate-950 group-data-[state=open]/pending-app:block group-data-[state=open]/pending-app:text-slate-950`}
              strokeWidth={1.5}
            />
            <Text className={labelClassName}>Not on app</Text>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuItem
            disabled={!appDownloadUrl}
            onClick={handleCopyAppDownloadUrl}
          >
            <CopyIcon className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
            Copy app download link
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderNotOnApp = () => {
    return (
      <Row className="items-center gap-2">
        <PhoneOff
          className="h-4 w-4 shrink-0 text-slate-400"
          strokeWidth={1.5}
        />
        <Text className="text-xs font-normal text-slate-500">Not on app</Text>
      </Row>
    );
  };

  const render = () => {
    if (!hasSignedUp) {
      return isReadOnly ? renderNotOnApp() : renderPendingUserDropdown();
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{renderAppUser()}</TooltipTrigger>
          <TooltipContent>User has signed up with Laborup</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return render();
};
